import { type Row, type RowSelectionState } from '@tanstack/react-table';
import { useCallback } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTableRowSelectionOptions,
  DataTableRowSelectionProps,
} from './row-selection-types.js';

const fallbackState = {};

/**
 * Converts the public `Record<string, boolean>` selection shape into the
 * `Record<string, true>` that TanStack Table v9.0.0-beta.52 expects, where an absent key is
 * the only way to express "not selected".
 */
function toRowSelectionState(
  selectedRows: Record<string, boolean>,
): RowSelectionState {
  const state: RowSelectionState = {};
  for (const [rowId, isSelected] of Object.entries(selectedRows)) {
    if (isSelected) {
      state[rowId] = true;
    }
  }
  return state;
}

/**
 * Configuration hook for the DataTable UserActions feature
 * @internal
 */
export function useRowSelection<TData extends DataTableRowData>(
  props: DataTableRowSelectionProps<TData>,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const configSelectableRows = useDataTableConfigElement('selectableRows');
  // Need to move that into a const here in order to make the typechecks
  // work, because otherwise the selectableRows member could switch.

  // note: for case where the selectableRows prop is an object, it needs
  // to be memoized by the consumer, to avoid updates if it is identical
  const selectableRows = props.selectableRows ?? configSelectableRows;
  // options.enableRowSelection is used as dependency in useBuiltinColumns, therefore we need to cache this function to avoid infinite rerenders
  const enableRowSelectionCallback = useCallback(
    (row: Row<TDataTableFeatures, Exclude<TData, unknown>>) => {
      // If there is a function defined that will allow for disabling certain
      // row selections, we can apply this one.
      if (
        selectableRows !== undefined &&
        typeof selectableRows !== 'boolean' &&
        selectableRows.disableRowSelection
      ) {
        return !selectableRows.disableRowSelection(row.original);
      }
      return true;
    },
    [selectableRows],
  );

  if (selectableRows !== undefined) {
    // oxlint-disable-next-line react/immutability
    options.enableRowSelection = true;
    let rowSelectionMode: DataTableRowSelectionOptions<
      TDataTableFeatures,
      Row<TDataTableFeatures, Exclude<TData, unknown>>
    >['rowSelectionSelectAllMode'] = 'page';

    // If selectableRows is controlled via a boolean, we can default the options
    if (selectableRows === true || selectableRows === false) {
      // oxlint-disable-next-line react/immutability
      options.enableRowSelection = selectableRows;
    } else {
      const { selectAllBehavior, limit } = selectableRows;

      // oxlint-disable-next-line react/immutability
      options.enableRowSelection = enableRowSelectionCallback;

      // Change the rowSelectionMode for the _select all_ checkbox if it is defined.
      if (selectAllBehavior) {
        rowSelectionMode = selectAllBehavior;
      }

      // Defines a row selection limit for the maximum count of rows that are
      // selectable.
      // oxlint-disable-next-line react/immutability
      options.rowSelectionLimit =
        limit !== undefined && limit >= 0 ? limit : undefined;
    }

    // oxlint-disable-next-line react/immutability
    options.enableMultiRowSelection = true;
    // oxlint-disable-next-line react/immutability
    options.rowSelectionSelectAllMode = rowSelectionMode;
  } else {
    // oxlint-disable-next-line react/immutability
    options.enableRowSelection = false;
  }

  const [selectedRows, setSelectedRows] = useControllableState<
    Record<string, boolean>
  >({
    value: props.selectedRows,
    defaultValue: props.defaultSelectedRows,
    onChange: props.onRowSelectionChange,
  });

  // oxlint-disable-next-line react/immutability
  options.state = {
    ...options.state,
    rowSelection: selectedRows
      ? toRowSelectionState(selectedRows)
      : fallbackState,
  };

  // oxlint-disable-next-line react/immutability
  options.onRowSelectionChange = useCallback(
    (
      updater:
        | RowSelectionState
        | ((state: RowSelectionState) => RowSelectionState),
    ) => {
      const state = selectedRows ? toRowSelectionState(selectedRows) : {};
      const newValue = typeof updater === 'function' ? updater(state) : updater;
      setSelectedRows(newValue);
    },
    [setSelectedRows, selectedRows],
  );
}
